/**
 * `<FurnaceErrorBanner>` — actionable recovery banner for a failed
 * Furnace build (Bridge-3 UX layer).
 *
 * Renders ABOVE the chat panel when the most-recent agent turn for
 * the active game terminated with an error AND the routing gate was
 * open (in-cohort + flag-on). The banner surfaces:
 *
 *   - A short, plain-English title naming the failure class.
 *   - A recovery hint pointing the engineer at the right next step.
 *   - A primary CTA — `'retry'` re-dispatches the failing turn,
 *     `'open-diagnostics'` jumps to Settings → Diagnostics,
 *     `'check-cohort'` jumps to Settings → Advanced → Furnace status,
 *     `'check-connections'` jumps to Settings → Connections.
 *   - The always-on `#foundry` escalation pointer.
 *
 * Privacy contract: every user-facing string in the banner comes
 * from the classified `FurnaceErrorView`, which the mapper guarantees
 * is free of infrastructure URLs by construction. The raw error
 * message is held only in `view.diagnostic` and is NOT rendered.
 *
 * Renders `null` when `view === null`. Parent controls the latch
 * (it resets on next-turn dispatch + game switch).
 *
 * British spelling throughout.
 */

import type {
  FurnaceErrorPrimaryCta,
  FurnaceErrorView,
} from '../workspace/furnace-error-mapping';

/** User-facing label for each primary CTA. */
const PRIMARY_CTA_LABELS: Record<FurnaceErrorPrimaryCta, string> = {
  retry: 'Retry',
  'open-diagnostics': 'Open Diagnostics',
  'check-cohort': 'Check cohort',
  'check-connections': 'Check connections',
};

export interface FurnaceErrorBannerProps {
  /** Classified error view from `classifyFurnaceError`. `null` hides
   *  the banner. */
  view: FurnaceErrorView | null;
  /** Fires on the primary CTA click. The parent decides what to do
   *  per `view.primaryCta` — e.g. `'retry'` re-sends the failing
   *  turn, `'open-diagnostics'` navigates to Settings → Diagnostics. */
  onPrimaryCta?: (kind: FurnaceErrorPrimaryCta) => void;
  /** Fires when the engineer dismisses the banner. The parent
   *  clears the latch so the banner unmounts. */
  onDismiss?: () => void;
}

export function FurnaceErrorBanner({
  view,
  onPrimaryCta,
  onDismiss,
}: FurnaceErrorBannerProps): React.JSX.Element | null {
  if (view === null) return null;

  const primaryLabel = PRIMARY_CTA_LABELS[view.primaryCta];

  return (
    <div
      data-testid="furnace-error-banner"
      data-furnace-error-kind={view.kind}
      role="alert"
      className="mb-3 flex flex-wrap items-start justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm"
    >
      <div className="flex items-start gap-2 text-red-200">
        <span aria-hidden="true" className="mt-0.5">
          🔴
        </span>
        <div className="flex flex-col gap-1">
          <span
            data-testid="furnace-error-banner-title"
            className="font-medium"
          >
            {view.title}
          </span>
          <span
            data-testid="furnace-error-banner-hint"
            className="text-red-200/80"
          >
            {view.hint}
          </span>
          <span
            data-testid="furnace-error-banner-escalation"
            className="text-xs text-red-200/60"
          >
            {view.escalation}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-start gap-2">
        {onPrimaryCta && (
          <button
            type="button"
            data-testid="furnace-error-banner-primary-cta"
            data-cta-kind={view.primaryCta}
            onClick={() => onPrimaryCta(view.primaryCta)}
            className="rounded-md border border-red-300/40 bg-red-500/20 px-3 py-1 text-xs font-medium text-red-100 hover:bg-red-500/30"
          >
            {primaryLabel}
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            data-testid="furnace-error-banner-dismiss"
            aria-label="Dismiss"
            onClick={onDismiss}
            className="rounded-md border border-white/10 px-2 py-1 text-xs text-white/60 hover:bg-white/5"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
